import { CircleHelp, FolderOpen, GitFork, History, Keyboard, MousePointer2, Plus, Wrench } from "lucide-react";
import { useEffect, useRef } from "react";
import type { CanvasNodeKind } from "./nodes/node-types.js";
import type { CanvasToolDefinition, CanvasToolId } from "./canvas-tools.js";

const icons = { add: Plus, mode: MousePointer2, batch: GitFork, toolbox: Wrench, assets: FolderOpen, history: History, shortcuts: Keyboard, help: CircleHelp } as const;

export interface CanvasToolRailProps {
  tools: readonly CanvasToolDefinition[];
  activeTool: CanvasToolId | undefined;
  onOpenTool(tool: CanvasToolId | undefined): void;
  onSetCanvasTool(tool: "select" | "pan"): void;
  onAdd(kind: CanvasNodeKind): void;
  onBatch(): void;
  onSaveWorkflow(): void;
  onInsertWorkflow(): void;
  canInsertWorkflow: boolean;
  workflows: readonly { id: string; name: string }[];
  selectedWorkflowId: string | undefined;
  onSelectWorkflow(workflowId: string | undefined): void;
  onOpenAssets(): void;
  onOpenHistoryAssets(): void;
}

export function CanvasToolRail(props: CanvasToolRailProps) {
  const triggers = useRef<Partial<Record<CanvasToolId, HTMLButtonElement>>>({});
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !props.activeTool) return;
      event.preventDefault();
      const active = props.activeTool;
      props.onOpenTool(undefined);
      queueMicrotask(() => triggers.current[active]?.focus());
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [props.activeTool, props.onOpenTool]);
  const close = () => {
    const active = props.activeTool;
    props.onOpenTool(undefined);
    if (active) queueMicrotask(() => triggers.current[active]?.focus());
  };
  const add = (kind: CanvasNodeKind) => { props.onAdd(kind); close(); };
  return (
    <div className="studio-tool-rail-wrap">
      {props.activeTool ? <section className="studio-tool-rail-popover" role="dialog" aria-label={`${props.activeTool === "add" ? "添加节点和资源" : props.tools.find((tool) => tool.id === props.activeTool)?.label ?? "画布工具"}菜单`}>
        <button type="button" className="studio-tool-rail-popover__close" aria-label="关闭工具菜单" onClick={close}>×</button>
        {props.activeTool === "add" ? <div className="studio-tool-rail-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => add("text")}>文本</button>
          <button type="button" role="menuitem" onClick={() => add("script")}>手动脚本</button>
          <button type="button" role="menuitem" onClick={() => add("image")}>上传图片</button>
          <button type="button" role="menuitem" onClick={() => add("video")}>上传视频</button>
          <button type="button" role="menuitem" onClick={() => add("audio")}>上传音频</button>
          <button type="button" role="menuitem" onClick={() => { props.onOpenAssets(); close(); }}>素材库</button>
          <button type="button" role="menuitem" onClick={() => add("seedance")}>Seedance 生成</button>
        </div> : null}
        {props.activeTool === "mode" ? <div className="studio-tool-rail-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => { props.onSetCanvasTool("select"); close(); }}>框选节点（V）</button>
          <button type="button" role="menuitem" onClick={() => { props.onSetCanvasTool("pan"); close(); }}>移动画布（H）</button>
        </div> : null}
        {props.activeTool === "batch" ? <div><p>从框选输入节点的统一＋拖出连线，或直接新建 Seedance。</p><button type="button" onClick={() => { props.onBatch(); close(); }}>选择 Seedance 模型</button></div> : null}
        {props.activeTool === "toolbox" ? <div className="studio-tool-rail-menu studio-tool-rail-menu--toolbox">
          <button type="button" onClick={() => { props.onSaveWorkflow(); close(); }}>保存为私有工作流</button>
          {props.workflows.length > 0 ? (
            <label className="studio-tool-rail-workflow-select">
              选择要插入的私有工作流
              <select
                aria-label="选择要插入的私有工作流"
                value={props.selectedWorkflowId ?? ""}
                onChange={(event) => props.onSelectWorkflow(event.currentTarget.value || undefined)}
              >
                {props.workflows.map((workflow) => <option key={workflow.id} value={workflow.id}>{workflow.name}</option>)}
              </select>
            </label>
          ) : <p>暂无私有工作流</p>}
          <button type="button" disabled={!props.canInsertWorkflow} onClick={() => { props.onInsertWorkflow(); close(); }}>插入私有工作流</button>
        </div> : null}
        {props.activeTool === "shortcuts" ? <dl><dt>V</dt><dd>框选节点</dd><dt>H</dt><dd>移动画布</dd><dt>Escape</dt><dd>关闭当前菜单</dd><dt>Ctrl/⌘ Z</dt><dd>撤销</dd></dl> : null}
        {props.activeTool === "help" ? <div><p>框选输入节点后，拖动右侧统一＋到 Seedance；拖到空白画布会弹出 Seedance 模型选择。</p><p>连线可右键删除，也可撤销。素材默认仅自己可见，明确共享后才会进入团队素材库。</p></div> : null}
      </section> : null}
      <nav className="studio-canvas-tool-rail" aria-label="画布快捷工具" role="toolbar">
        {props.tools.map((tool) => {
          const Icon = icons[tool.id];
          return <button key={tool.id} ref={(element) => { if (element) triggers.current[tool.id] = element; else delete triggers.current[tool.id]; }} type="button" aria-label={tool.ariaLabel} title={tool.label} disabled={tool.disabled} aria-pressed={props.activeTool === tool.id} onClick={() => {
            if (tool.id === "assets") {
              props.onOpenTool(undefined);
              props.onOpenAssets();
              return;
            }
            if (tool.id === "history") {
              props.onOpenTool(undefined);
              props.onOpenHistoryAssets();
              return;
            }
            props.onOpenTool(props.activeTool === tool.id ? undefined : tool.id);
          }}><Icon aria-hidden="true" size={18} /></button>;
        })}
      </nav>
    </div>
  );
}
